import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useBlocker } from "react-router-dom";
import { Image, Input, Tooltip, Upload } from "antd";
import type { UploadProps, UploadFile } from "antd";
import routeNames from "constants/route-names";
import { useAddAiChatFeedbackMutation } from "graphql/mutations/add-ai-chat-feedback.generated";
import {
	FileType,
	getBase64,
	uploadFeedbackAttachments,
	deleteFeedbackAttachments,
	createBeforeUpload,
	type FeedbackAttachment
} from "utils/files-management";

import { ReactComponent as ThumbsDownIcon } from "../../assets/images/thumbs-down.svg";
import { ReactComponent as ThumbsUpIcon } from "../../assets/images/thumbs-up.svg";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";

interface AiChatFeedbackProps {
	doctrineChatId: string;
	doctrineChatMessageId: string;
	onFeedbackChange?: (wasIssueResolved: boolean | null) => void;
}

const AiChatFeedback: React.FC<AiChatFeedbackProps> = ({ doctrineChatId, doctrineChatMessageId, onFeedbackChange }) => {
	const { loggedUserValue } = useLoggedUser();
	const [selectedFeedback, setSelectedFeedback] = useState<boolean | null>(null);
	const [feedback, setFeedback] = useState<string>("");
	const [previewOpen, setPreviewOpen] = useState(false);
	const [previewImage, setPreviewImage] = useState("");
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [isReviewComplete, setIsReviewComplete] = useState(false);
	const [isUploadingReview, setIsUploadingReview] = useState(false);
	const [uploadedAttachments, setUploadedAttachments] = useState<FeedbackAttachment[]>([]);
	const [feedbackId, setFeedbackId] = useState<string | null>(null);
	const uploadedAttachmentsRef = useRef(uploadedAttachments);
	const blocker = useBlocker(
		useCallback(() => !isReviewComplete && selectedFeedback !== null, [isReviewComplete, selectedFeedback])
	);

	const handlePreview = useCallback(
		async (file: UploadFile) => {
			if (!file.url && !file.preview) {
				file.preview = await getBase64(file.originFileObj as FileType);
			}

			setPreviewImage(file.url || (file.preview as string));
			setPreviewOpen(true);
		},
		[setPreviewImage, setPreviewOpen]
	);

	const handleChange: UploadProps["onChange"] = useCallback(
		({ fileList: newFileList }) => {
			// There is some weird behavior with the Upload component
			// where it sets the file upload status to 'error' even if the upload was successful.
			const alteredFileList: UploadFile<any>[] = newFileList.map(file => {
				if (file.status === "error" && file.originFileObj) {
					return {
						...file,
						status: "done" // Set status to 'done' if the file was uploaded successfully
					};
				}
				return file;
			});

			setFileList(alteredFileList);
		},
		[setFileList]
	);

	const [addAiChatFeedback] = useAddAiChatFeedbackMutation({
		onError: error => {
			console.error("Error adding ai chat feedback:", error);
			alert("There was an error submitting your feedback. Please try again later.");
			setIsUploadingReview(false);
		}
	});

	const handleThumbs = useCallback(
		(wasIssueResolved: boolean) => {
			setSelectedFeedback(wasIssueResolved);
			onFeedbackChange?.(wasIssueResolved);
			addAiChatFeedback({
				variables: {
					input: {
						id: feedbackId || undefined,
						was_issue_resolved: wasIssueResolved,
						created_by_user_id: loggedUserValue.id,
						doctrine_chat_id: doctrineChatId,
						doctrine_chat_message_id: doctrineChatMessageId
					}
				}
			}).then(({ data }) => {
				setFeedbackId(data?.insert_ai_chat_feedback_one?.id || null);
			});
		},
		[feedbackId, loggedUserValue.id, doctrineChatId]
	);

	const handleSubmit = useCallback(async () => {
		if (isUploadingReview || selectedFeedback === null) return;
		setIsUploadingReview(true);

		const attachments = await uploadFeedbackAttachments({
			fileList,
			uploadedAttachments: uploadedAttachments,
			bucketName: "ai-chat-feedback-attachments",
			fileNamePrefix: `ai-chat-${doctrineChatId}`,
			setUploadedAttachments: setUploadedAttachments
		});

		if (attachments === undefined) {
			setIsUploadingReview(false);
			return;
		}

		addAiChatFeedback({
			variables: {
				input: {
					id: feedbackId || undefined,
					notes: feedback,
					was_issue_resolved: selectedFeedback,
					created_by_user_id: loggedUserValue.id,
					doctrine_chat_id: doctrineChatId,
					doctrine_chat_message_id: doctrineChatMessageId,
					ai_chat_feedback_attachments: {
						data: attachments.map(({ storageRef, uid, ...ua }) => ua)
					}
				}
			}
		}).then(() => {
			// Since the review is successful, no need to track the uploaded attachments anymore
			setUploadedAttachments([]);
			setIsReviewComplete(true);
		});
	}, [
		isUploadingReview,
		uploadedAttachments,
		doctrineChatId,
		selectedFeedback,
		feedback,
		fileList,
		feedbackId,
		loggedUserValue.id
	]);

	useEffect(() => {
		uploadedAttachmentsRef.current = uploadedAttachments;
	}, [uploadedAttachments]);

	// This is to ensure that if the user navigates away without submitting, we clean up any uploaded files
	// trick? courtesy of copilot
	useEffect(() => {
		return () => deleteFeedbackAttachments(uploadedAttachmentsRef.current);
	}, []);

	useEffect(() => {
		if (blocker.state === "blocked") {
			const userContrl = window.confirm("You have unsaved feedback. Are you sure you want to leave this page?");
			if (userContrl) {
				deleteFeedbackAttachments(uploadedAttachmentsRef.current);
				blocker.proceed();
			} else {
				blocker.reset();
			}
		}
	}, [blocker]);

	if (!doctrineChatId) {
		return null;
	}

	if (selectedFeedback === null) {
		return (
			<div className="acf-ai-chat-feedback">
				<div className="acf-buttons-container">
					<div className="acf-button">
						<Tooltip
							overlayClassName="acf-tooltip"
							color="black"
							placement="bottom"
							title="Give positive feedback"
							mouseLeaveDelay={0}
						>
							<ThumbsUpIcon
								className={selectedFeedback ? "acf-g-feedback-selected-icon" : "acf-g-feedback-icon"}
								height={16}
								width={16}
								onClick={() => handleThumbs(true)}
							/>
						</Tooltip>
					</div>
					<div className="acf-button">
						<Tooltip
							overlayClassName="acf-tooltip"
							color="black"
							placement="bottom"
							title="Give negative feedback"
							mouseLeaveDelay={0}
						>
							<ThumbsDownIcon
								className={selectedFeedback === false ? "acf-g-feedback-selected-icon" : "acf-g-feedback-icon"}
								height={16}
								width={16}
								onClick={() => handleThumbs(false)}
							/>
						</Tooltip>
					</div>
				</div>
			</div>
		);
	}

	if (isReviewComplete) {
		return (
			<div className="acf-ai-chat-feedback">
				<div className="acf-title">Thanks for the feedback.</div>
				<div className="acf-buttons-container" style={{ justifyContent: "center" }}>
					<div className="acf-button" style={{ cursor: "default" }}>
						<ThumbsUpIcon className="acf-g-feedback-icon" height={24} width={24} />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="acf-ai-chat-feedback">
			<div className="acf-feedback-section">
				<div className="acf-feedback-prompt">
					{selectedFeedback ? "Great! We love positive feedback!" : "How can we improve?"}
					<img
						src={`${process.env.PUBLIC_URL}/assets/images/icons/x-2.svg`}
						alt="Close"
						style={{ cursor: "pointer" }}
						onClick={() => {
							setSelectedFeedback(null);
							onFeedbackChange?.(null);
						}}
						height={16}
						width={16}
					/>
				</div>
				<Input.TextArea
					className="product-details-feedback-wrapper"
					name="product-details-feedback"
					placeholder={
						"Can you tell us more about what was helpful? (optional)\n\nYour response will remain anonymous."
					}
					value={feedback}
					autoComplete="off"
					onChange={e => setFeedback(e.target.value)}
					allowClear
					rows={3}
				/>
			</div>

			<div className="acf-feedback-section">
				Add videos or pictures to share a helpful tip or improvement suggestion.
				<div className="product-details-feedback-upload">
					{previewImage && (
						<Image
							wrapperStyle={{ display: "none" }}
							preview={{
								visible: previewOpen,
								onVisibleChange: visible => setPreviewOpen(visible),
								afterOpenChange: visible => !visible && setPreviewImage("")
							}}
							src={previewImage}
						/>
					)}
					<Upload
						listType="picture-card"
						fileList={fileList}
						onPreview={handlePreview}
						onChange={handleChange}
						accept="image/*,video/*"
						beforeUpload={createBeforeUpload({
							allowedTypePattern: /^image\/|^video\//,
							onInvalidType: () => alert("Only images and videos are allowed.")
						})}
						maxCount={5}
						multiple
					>
						{fileList.length < 5 && (
							<button style={{ border: 0, background: "none" }} type="button">
								<img src={`${process.env.PUBLIC_URL}/assets/images/icons/plus-circle.svg`} alt="Upload Icon" />
							</button>
						)}
					</Upload>
				</div>
			</div>

			<div className="acf-actions-row">
				<div className="acf-request-service-link">
					{!selectedFeedback && (
						<span className="link-label">
							Require further assistance?&nbsp;
							<Link
								to={{
									pathname: routeNames.REQUEST_SERVICE
								}}
								state={{ fromAiChat: true }}
								className="inline-link"
								rel="noopener"
							>
								{"Request Service"}
							</Link>
						</span>
					)}
				</div>
				<button className="acf-submit-button" onClick={() => handleSubmit()} disabled={isUploadingReview}>
					Submit
				</button>
			</div>
		</div>
	);
};

export default AiChatFeedback;
